import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { parse, intervalToDuration } from 'date-fns';
import MobileSort from './MobileSort';
import FlightDurationMobile from './filters/FlightDurationMobile';
import MobileNights from './filters/MobileNights';
import MobileStars from './filters/MobileStars';
import MobileFilterItem from './filters/MobileFilterItem';
import FlexMobile from './filters/FlexMobile';
import Button from '../../components/Button';
import { t } from '../../utilities/translator';
import { selectSecondaryFilter, updateAllSecondaryFilter } from './secondaryFilterSlice';
import { scrollToResults } from '../../utilities/helpers';
import { updateDatesFilter } from '../mainFilter/mainFilterSlice';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { selectDateFilter, selectFilterData } from '../mainFilter/selectors';
import { updateSecondaryFilterModal } from './secondaryFilterModalsSlice';
import { clearPrefetchDeals } from '../dealsList/prefetchDealsSlice';
import { filterClearClick, filterSaveClick } from '../../appSlice';
import abTesting from '../../services/abTesting';

const MobileSecondaryFilter = () => {
  const dispatch = useDispatch();
  const { data, pending, error } = useSelector(selectSecondaryFilter);
  const { searchEngine } = useSelector(selectFilterData);
  const datesFilter = useSelector(selectDateFilter);
  const [localState, setLocalState] = useState(data);
  const [lastChangedFilter, setLastChangedFilter] = useState('');
  const { when } = searchEngine;
  const { type, startDate, endDate } = when || {};

  const maxNights = when?.maxNights;
  const minNights = when?.minNights;

  let daysInterval = 20;

  if (type === 'range' && startDate && endDate) {
    daysInterval = intervalToDuration({
      start: parse(startDate, 'dd/MM/yyyy', new Date()),
      end: parse(endDate, 'dd/MM/yyyy', new Date()),
    }).days;
  }

  const [nights, setNights] = useState(
    datesFilter.initial.nightsList.map((night) => ({
      ...night,
      isSelected: minNights === night.value || maxNights === night.value,
    }))
  );

  useBodyScrollLock({ condition: true, mobileOnly: true });

  useEffect(() => {
    setNights(
      when && when.nights
        ? nights.map((night) => ({
            ...night,
            isSelected: when.nights.includes(night.value),
          }))
        : datesFilter.initial.nightsList
    );
  }, [maxNights, minNights, when, datesFilter.initial.nightsList]);

  if (pending) {
    return <div className="secondary-filter">Loading...</div>;
  }

  if (error) {
    return <div className="secondary-filter">Some error. Try to reload the page.</div>;
  }

  const changeNights = (value) => () => {
    if (daysInterval && daysInterval >= value) {
      setNights(
        nights.map((night) => ({
          ...night,
          isSelected: value === night.value ? !night.isSelected : night.isSelected,
        }))
      );
    }
  };

  const activeNights = nights.filter(({ isSelected }) => isSelected).map(({ value }) => value);

  const saveNights = () => {
    const minNights = isFinite(Math.min.apply(null, activeNights)) ? Math.min.apply(null, activeNights) : 0;
    const maxNights = isFinite(Math.max.apply(null, activeNights)) ? Math.max.apply(null, activeNights) : 0;

    const dates = {
      ...when,
      startDate: when.startDate ? when.startDate : null,
      endDate: when.endDate ? when.endDate : null,
      minNights: minNights !== 0 ? minNights : undefined,
      maxNights: maxNights !== 0 ? maxNights : undefined,
      nights: activeNights,
    };
    dispatch(updateDatesFilter(dates));
  };

  const clearNights = () => {
    const dates = {
      ...when,
      minNights: undefined,
      maxNights: undefined,
      nights: [],
    };
    dispatch(updateDatesFilter(dates));
  };

  const handleCheckbox = (filterName) => {
    if (filterName === 'duration') {
      return (duration) => {
        setLastChangedFilter(filterName);
        setLocalState({
          ...localState,
          [filterName]: duration,
        });
      };
    }
    if (filterName === 'flex') {
      return (checked) => {
        const newVal = { ...localState.flex, active: checked };
        setLastChangedFilter(filterName);
        setLocalState({
          ...localState,
          [filterName]: newVal,
        });
      };
    }
    if (filterName === 'sort') {
      return (index) => {
        setLastChangedFilter(filterName);
        setLocalState({
          ...localState,
          [filterName]: localState[filterName].map((item, i) =>
            index === i ? { ...item, active: true } : { ...item, active: false }
          ),
        });
      };
    }
    return (index) => {
      setLastChangedFilter(filterName);
      setLocalState({
        ...localState,
        [filterName]: localState[filterName].map((item, i) => (index === i ? { ...item, active: !item.active } : item)),
      });
    };
  };

  const onSaveClick = () => {
    dispatch(updateSecondaryFilterModal({ mobile: false }));
    dispatch(clearPrefetchDeals());
    dispatch(filterSaveClick());
    saveNights();
    dispatch(
      updateAllSecondaryFilter({
        updatedFilters: localState,
        lastChangedFilter,
      })
    );
    scrollToResults();
  };

  const onClear = () => {
    const { board, duration, facilities, rating, sort, stars, stops, flex, luggage } = data;
    const { minTime, maxTime } = duration;

    dispatch(filterClearClick());
    dispatch(updateSecondaryFilterModal({ mobile: false }));
    clearNights();
    dispatch(
      updateAllSecondaryFilter({
        updatedFilters: {
          board: board.map((item) => ({ ...item, active: false })),
          stars: stars.map((item) => ({ ...item, active: false })),
          sort: sort.map((item, ind) => (ind === 0 ? { ...item, active: true } : { ...item, active: false })),
          rating: rating.map((item) => ({ ...item, active: false })),
          facilities: facilities.map((item) => ({ ...item, active: false })),
          stops: stops.map((item) => ({ ...item, active: false })),
          duration: { activeTime: maxTime, minTime, maxTime },
          flex: abTesting.moveMultiRates ? { ...flex } : { ...flex, active: false },
          luggage: luggage
        },
      })
    );
    scrollToResults();
  };

  return (
    <div className="secondary-filter-popup">
      <div className="secondary-filter-popup__wrap">
        <div className="secondary-filter-popup__header">{t('secondaryFilter.Sort_Filter')}</div>
        <div className="secondary-filter-popup__body">
          <MobileSort sort={localState.sort} handleCheckbox={handleCheckbox('sort')} />
          <MobileStars localFilter={localState.stars} handleCheckbox={handleCheckbox('stars')} />
          <MobileNights changeNights={changeNights} nights={nights} daysInterval={daysInterval} />
          <MobileFilterItem
            name={t('secondaryFilter.Stops')}
            localFilter={localState.stops}
            handleCheckbox={handleCheckbox('stops')}
          />
          <FlightDurationMobile duration={localState.duration} handleChange={handleCheckbox('duration')} />

          {!abTesting.moveMultiRates && <FlexMobile flex={localState.flex} handleCheckbox={handleCheckbox('flex')} />}

          <MobileFilterItem
            name={t('secondaryFilter.Board_type')}
            localFilter={localState.board}
            handleCheckbox={handleCheckbox('board')}
          />
          <MobileFilterItem
            name={t('secondaryFilter.Ameneties')}
            localFilter={localState.facilities}
            filterName={'facilities'}
            handleCheckbox={handleCheckbox('facilities')}
          />
        </div>
        <div className="secondary-filter-popup__footer">
          <button
            className="icon-close filter-popup__close"
            onClick={() => dispatch(updateSecondaryFilterModal({ mobile: false }))}
          />
          <div className="btn-group">
            <Button type="tertiary" onClick={onClear} label={t('buttons.Clear_all')} />
            <Button type="secondary" onClick={onSaveClick} label={t('buttons.Apply')} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileSecondaryFilter;
